import { Callout, HelpLink, Related } from "./parts";

const INSTALL_HELPER = `pipx install "git+https://github.com/MobileAWS/nexus-proxy.git#subdirectory=tools/nexus-aws-token"
command -v nexus-aws-token      # note the absolute path, e.g. ~/.local/bin/nexus-aws-token

aws sso login --profile dev
curl -fsS "https://ai-proxy.example.com/v1/models" \\
  -H "Authorization: Bearer $(nexus-aws-token --profile dev --region us-east-1 --audience <installation id>)"`;

const CLAUDE_SETTINGS = `{
  "env": {
    "ANTHROPIC_BASE_URL": "https://ai-proxy.example.com",
    "CLAUDE_CODE_API_KEY_HELPER_TTL_MS": "300000",
    "CLAUDE_CODE_DISABLE_NONESSENTIAL_TRAFFIC": "1"
  },
  "apiKeyHelper": "/home/you/.local/bin/nexus-aws-token --profile dev --region us-east-1 --audience 0192f3a1-4c80-7a1e-8f3b-2c9d5e7a1b04"
}`;

const CLAUDE_KEY = `export ANTHROPIC_BASE_URL="https://ai-proxy.example.com"
export ANTHROPIC_AUTH_TOKEN="nxk_…"      # sent as Authorization: Bearer
unset ANTHROPIC_API_KEY
claude`;

const CODEX_CONFIG = `# ~/.codex/config.toml (or ~/.codex/nexus.config.toml and \`codex --profile nexus\`)
model_provider = "nexus"
model = "gpt-6-astra"

[model_providers.nexus]
name = "Nexus proxy"
base_url = "https://ai-proxy.example.com/v1"
wire_api = "responses"

[model_providers.nexus.auth]
command = "/home/you/.local/bin/nexus-aws-token"
args = ["--profile", "dev", "--region", "us-east-1", "--audience", "0192f3a1-4c80-7a1e-8f3b-2c9d5e7a1b04"]
timeout_ms = 10000
refresh_interval_ms = 300000`;

const CODEX_KEY = `[model_providers.nexus]
name = "Nexus proxy"
base_url = "https://ai-proxy.example.com/v1"
env_key = "NEXUS_KEY"          # the variable holding the nxk_ key
wire_api = "responses"`;

export function ClaudeCodeAndCodex() {
  return (
    <>
      <p>
        Developers point their tools at the customer's Nexus proxy instead of Anthropic or OpenAI. This page is what to hand
        them. The full developer guide is <code>docs/guides/client-setup.md</code> in the Nexus repository.
      </p>
      <p>
        The simplest route: send developers to <code>https://&lt;proxy&gt;/login</code>. After they sign in, the page gives
        them one key for both tools and a one-time command that configures Claude Code and Codex. For them or their coding
        agent, <code>https://&lt;proxy&gt;/setup</code> (also <code>/setup.md</code>) has step-by-step setup instructions
        that contain no secret, for example{" "}
        <a href="https://ai-proxy.example.com/setup" target="_blank" rel="noreferrer">
          https://ai-proxy.example.com/setup
        </a>
        .
      </p>

      <h2>What to give the developer</h2>
      <table>
        <thead>
          <tr>
            <th>Value</th>
            <th>Example</th>
            <th>Where you find it</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Proxy URL</td>
            <td>
              <code>https://ai-proxy.example.com</code>
            </td>
            <td>The proxy stack's output, from the install</td>
          </tr>
          <tr>
            <td>Installation id (the audience)</td>
            <td>
              <code>0192f3a1-4c80-…</code>
            </td>
            <td>The customer's AWS accounts tab</td>
          </tr>
          <tr>
            <td>The proxy's AWS region</td>
            <td>
              <code>us-east-1</code>
            </td>
            <td>The installation's region</td>
          </tr>
        </tbody>
      </table>
      <p>
        With AWS sign-in (recommended), that is all: developers install the <code>nexus-aws-token</code> helper and sign
        in to AWS as usual. With API keys, they also need an <code>nxk_…</code> key. See{" "}
        <HelpLink to="users-teams-and-sign-in" />.
      </p>
      <pre>{INSTALL_HELPER}</pre>

      <h2 id="claude-code">Claude Code</h2>
      <p>
        Put the proxy and the helper in <code>~/.claude/settings.json</code>. Use the helper's absolute path. The proxy URL
        has no <code>/v1</code>.
      </p>
      <pre>{CLAUDE_SETTINGS}</pre>
      <ul>
        <li>
          Leave <code>ANTHROPIC_AUTH_TOKEN</code> and <code>ANTHROPIC_API_KEY</code> unset; either would be used instead of
          the helper.
        </li>
        <li>
          Keep <code>CLAUDE_CODE_API_KEY_HELPER_TTL_MS</code> at five minutes: each token lives 15 minutes and the helper
          always returns one with at least seven left.
        </li>
        <li>
          Run <code>aws sso login</code> in a terminal when the AWS session ends; Claude Code runs the helper in the
          background, where it cannot open a browser.
        </li>
        <li>
          Check: <code>/status</code> shows the proxy as the Anthropic base URL, and{" "}
          <code>claude -p "reply with the single word: ok"</code> answers.
        </li>
      </ul>
      <p>With an API key instead:</p>
      <pre>{CLAUDE_KEY}</pre>
      <p>
        For a whole company, IT can push the same settings as Claude Code managed settings.
      </p>
      <p>
        No model settings are needed. Claude Code's default and <code>/model opus</code>, <code>sonnet</code> and{" "}
        <code>haiku</code> work as they are (they ask for <code>claude-opus-5-5</code>, <code>claude-sonnet-5</code> and{" "}
        <code>claude-haiku-4-5</code>, which the proxy serves), as does any Claude model id the customer's Bedrock offers.
      </p>
      <Callout>
        Never put an <code>nxk_</code> key in a managed or shared project settings file: it is one person's key, billed to
        that person, on everyone's machine.
      </Callout>

      <h2 id="codex">Codex</h2>
      <p>
        Define the proxy as a model provider. Codex's URL <strong>does</strong> end in <code>/v1</code>. Codex runs the
        helper for its token and re-runs it every five minutes:
      </p>
      <pre>{CODEX_CONFIG}</pre>
      <p>
        With an API key, use <code>env_key</code> instead of the <code>auth</code> table (never both), and export the key
        in <code>NEXUS_KEY</code>:
      </p>
      <pre>{CODEX_KEY}</pre>
      <ul>
        <li>
          <code>wire_api = "responses"</code> is the only value current Codex supports.
        </li>
        <li>
          <code>gpt-6-astra</code> is the default, as in Codex itself. By default Codex may use the OpenAI models the
          proxy serves (<code>gpt-6-*</code>, <code>gpt-5.6-*</code>, <code>gpt-5.5</code>, <code>gpt-5.4</code>,{" "}
          <code>gpt-oss-*</code>) and Claude Code the Anthropic ones; the installation&apos;s{" "}
          <code>platform_providers_json</code> can let either use both, or other Bedrock models.
        </li>
        <li>The provider id must not be <code>openai</code>, <code>ollama</code> or <code>lmstudio</code>.</li>
        <li>
          Check: <code>codex exec "reply with the single word: ok"</code> (add <code>--profile nexus</code> for a profile
          file).
        </li>
      </ul>

      <h2>When it does not work</h2>
      <ul>
        <li>
          <strong>401</strong>: the credential was refused. Check the tool really uses the proxy URL, that the audience is
          this installation's id, and that the developer's AWS identity is allowed on the customer's{" "}
          <strong>Sign-in → AWS sign-in</strong> sub-tab.
        </li>
        <li>
          <strong>403</strong> on some requests: the key is limited to certain models, and the tool asked for another (often
          the background Haiku model, <code>claude-haiku-4-5</code>). Add that model to the key's allowed models.
        </li>
        <li>
          <strong>404</strong>: the path or the model name is not one the proxy serves. The proxy serves messages, chat
          completions, responses and the model list, nothing else.
        </li>
        <li>
          <strong>403</strong> <em>model … is not enabled for claude-code (or codex) on this proxy</em>: the model exists
          but is outside that tool's list; the message names the models it may use. Each tool's list is on the customer's{" "}
          <strong>Models → Agents</strong> sub-tab, within what <strong>Models → Families</strong> allows.
        </li>
      </ul>

      <h2 id="models-and-guardrails">Models and guardrails, as a developer sees them</h2>
      <p>
        The <strong>Models</strong> tab decides what each tool gets: by default every text model the installation's AWS
        account can invoke, from the providers chosen for the tool, as Bedrock offers them (<code>follow_aws</code>) —
        or a named allow-list. <code>/v1/models</code> shows a developer exactly their tool's list, the default first; a
        model outside it is refused 403 with the list, never served under another name.
      </p>
      <p>
        With a guardrail applied to the tool (the <strong>Guardrails</strong> tab), a blocked prompt is a 400 in the
        tool's own error shape carrying the blocked-input message plus "(blocked by your organization's Bedrock
        guardrail)"; a blocked answer is the blocked-output message with the dialect's refusal stop (Claude Code shows a
        refusal, Codex a content filter). The usage ledger records <code>guardrail_blocked</code> /{" "}
        <code>guardrail_intervened</code> and the policy categories that acted — never the text. A guardrail that cannot
        be reached fails closed: <code>503 guardrail_unavailable</code>, not an unguarded answer. Each block,
        intervention and failed-closed request is a <strong>Guardrail</strong> row on the customer's{" "}
        <strong>Activity → Log</strong>; the Guardrails tab holds the configuration only.
      </p>

      <h2 id="configuring-models">Configuring models</h2>
      <p>
        A customer's <strong>Models</strong> tab has four sub-tabs, each with its own address and its own Save:{" "}
        <strong>Inventory</strong>, <strong>Families</strong>, <strong>Fallbacks</strong> and <strong>Agents</strong>. The
        customer's proxy Admin shows the same four.
      </p>
      <ul>
        <li>
          <strong>Inventory</strong>: what an installation's AWS account can call. Choose the installation in the scope
          selector; at the organisation default each installation is a group of its own.
        </li>
        <li>
          <strong>Families</strong>: the models the whole organisation allows, per provider. Use <strong>Edit</strong> to
          pick <strong>Follow AWS</strong> (every model AWS lets the account call, including ones it adds later) or{" "}
          <strong>Choose models</strong> (only the ticked ones; a model AWS adds later stays off until it is ticked). The
          dialog's footer shows which agent defaults survive the change, and asks you to re-point any that would not.
        </li>
        <li>
          <strong>Fallbacks</strong>: the models the proxy tries, in order, when AWS has no capacity for a model (Bedrock
          answers 503 "unable to process" or throttles the request). A refusal is never retried. Each fallback is checked
          like a new request, starts with a cold prompt cache, and is billed as the model that answered. A stream falls back
          only before its first word.
        </li>
        <li>
          <strong>Agents</strong>: one card each for Claude Code, Codex, Web chat and Other tools. The chips are the models
          the agent is offered. The <strong>Default model</strong> must be one of them. <strong>Narrow for this agent</strong>{" "}
          gives one agent fewer providers or only some models. It can only take models away, never add one the org does not
          allow.
        </li>
      </ul>
      <p>
        <strong>What display names are.</strong> Each agent card's <strong>Advanced</strong> disclosure holds its display
        names: the name Claude Code, Codex or the chat shows in its model picker. Requests still go to the same model. A
        display name changes the label only, never routing or billing.
      </p>
      <Callout>
        Families and Fallbacks are set for the whole organisation. At an installation's scope they are shown read-only.
      </Callout>

      <Related to={["users-teams-and-sign-in", "installations"]} />
    </>
  );
}
